"use client";

import { MoonIcon, SunIcon } from "@radix-ui/react-icons";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/client";

interface ThemeSwitcherProps {
  compact?: boolean;
}

export function ThemeSwitcher({ compact = false }: ThemeSwitcherProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Check if on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Only show theme switcher after mounting to prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  // Show compact version on mobile or when compact prop is true
  const showCompact = compact || isMobile;
  const iconSize = showCompact ? "h-3.5 w-3.5" : "h-[1.2rem] w-[1.2rem]";

  // Toggle between light and dark
  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <Button
      variant={showCompact ? "ghost" : "outline"}
      size={showCompact ? "sm" : "icon"}
      onClick={toggleTheme}
      aria-label={t("common.theme.toggle")}
      className={`relative flex items-center justify-center ${showCompact ? "h-7 w-7 rounded-md p-1.5" : ""}`}
    >
      {resolvedTheme === "dark" ? (
        <SunIcon className={iconSize} />
      ) : (
        <MoonIcon className={iconSize} />
      )}
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}
